import React, { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import styled from "styled-components";

interface JustImageDropzoneProps {
  onMediaSelected: (selected: File | null) => void;
  base64: string;
  width?: number;
  height?: number;
  uploadMessage?: string;
}

const DropContainer = styled.div<{ isDragActive: boolean; isDragReject: boolean }>`
  height: 145px;
  border: 2px dashed #cccccc;
  border-radius: 4px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: ${({ isDragActive, isDragReject }) =>
    isDragActive ? "#e0f7fd" : isDragReject ? "#ffd7d7" : "white"};
  &:hover {
    border-color: #3884cc;
  }
`;

const MediaContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const UploadedImage = styled.img<{ width?: number; height?: number }>`
  width: ${({ width }) => (width ? `${width}px` : "auto")};
  height: ${({ height }) => (height ? `${height}px` : "auto")};
  object-fit: cover;
`;

const ChangeMediaButton = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  background-color: #fff;
  border: none;
  padding: 5px;
  cursor: pointer;
`;

const UploadMessage = styled.p`
  text-align: center;
  font-size: 16px;
  color: #999999;
  &:hover {
    color: #3884cc;
  }
`;

const ErrorMessage = styled.p`
  color: red;
  text-align: center;
`;

const JustImageDropzone: React.FC<JustImageDropzoneProps> = ({
  onMediaSelected,
  base64,
  width,
  height,
  uploadMessage,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(base64);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (base64) setSelectedImage(base64);
  }, [base64]);

  const convertBase64 = async (file: File): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => resolve(fileReader.result as string);
      fileReader.onerror = (error) => reject(error);
    });
  };

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 1) {
        setError("Você só pode enviar uma imagem.");
        setSelectedImage(null);
        onMediaSelected(null);
      } else if (acceptedFiles.length === 1) {
        if (!acceptedFiles[0].type.startsWith("image/")) {
          setError("Apenas imagens são permitidas.");
          setSelectedImage(null);
          onMediaSelected(null);
          return;
        }
        setError(null);
        const base64Result = await convertBase64(acceptedFiles[0]);
        setSelectedImage(base64Result);
        onMediaSelected(acceptedFiles[0]);
      }
    },
    [onMediaSelected]
  );

  const handleChangeImage = () => {
    setSelectedImage(null);
    setError(null);
    onMediaSelected(null);
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
    onDrop: handleDrop,
  });

  return (
    <DropContainer {...getRootProps({ isDragActive, isDragReject })}>
      <input {...getInputProps()} />
      {selectedImage ? (
        <MediaContainer>
          <UploadedImage src={selectedImage} width={width} height={height} />
          <ChangeMediaButton onClick={handleChangeImage}>
            Alterar imagem
          </ChangeMediaButton>
        </MediaContainer>
      ) : isDragActive ? (
        <UploadMessage>{uploadMessage || "Arraste o arquivo aqui..."}</UploadMessage>
      ) : (
        <UploadMessage
          dangerouslySetInnerHTML={{
            __html:
              uploadMessage ||
              "Arraste a imagem aqui ou clique para selecionar.<br/>(máx. 300x300 pixels)",
          }}
        />
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </DropContainer>
  );
};

export { JustImageDropzone };
